import React from 'react';
import Modal from './Modal';
import './PaymentResultModal.scss';
import { formatPrice } from '../Utils/formatting';

interface PaymentResultSuccessProps {
    isOpen: boolean;
    onClose: () => void;
    sessionId?: string;
    productName: string;
    price: number;
}

export const PaymentResultSuccess: React.FC<PaymentResultSuccessProps> = ({
    isOpen,
    onClose,
    sessionId,
    productName,
    price
}) => {
    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <div className="payment-result-modal success">
                <div className="result-icon success">
                    <svg viewBox="0 0 24 24" width="64" height="64">
                        <circle cx="12" cy="12" r="11" fill="#4CAF50" stroke="white" strokeWidth="2" />
                        <path d="M7 13l3 3 7-7" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>

                <h1 className="result-title">Payment Successful!</h1>
                
                <div className="result-details">
                    <p className="result-message">
                        Your Korean session has been successfully booked. 
                        We've sent a confirmation email with all the details.
                    </p>
                    
                    {sessionId && (
                        <p className="session-id">
                            Transaction ID: <span>{sessionId}</span>
                        </p>
                    )}

                    <div className="booking-summary">
                        <h2>Booking Details</h2>
                        <div className="summary-item">
                            <span className="label">Service:</span>
                            <span className="value">{productName}</span>
                        </div>
                        <div className="summary-item">
                            <span className="label">Price:</span>
                            <span className="value">{formatPrice(price)}</span>
                        </div>
                    </div>

                    <div className="next-steps">
                        <h2>What's Next?</h2>
                        <ul>
                            <li>Check your email for session details</li>
                            <li>Prepare for your session by reviewing basic phrases</li>
                            <li>Make sure your device's camera and microphone work correctly</li>
                            <li>Join your session 5 minutes before the scheduled time</li>
                        </ul>
                    </div>
                </div>

                <div className="action-buttons">
                    <button onClick={onClose} className="home-button">
                        Close
                    </button>
                </div>
            </div>
        </Modal>
    );
};

interface PaymentResultFailedProps {
    isOpen: boolean;
    onClose: () => void;
    errorMessage?: string;
    onRetry: () => void;
}

export const PaymentResultFailed: React.FC<PaymentResultFailedProps> = ({
    isOpen,
    onClose,
    errorMessage = 'Your payment could not be processed.',
    onRetry
}) => {
    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <div className="payment-result-modal failed">
                <div className="result-icon failed">
                    <svg viewBox="0 0 24 24" width="64" height="64">
                        <circle cx="12" cy="12" r="11" fill="#F44336" stroke="white" strokeWidth="2" />
                        <path d="M8 8l8 8M16 8l-8 8" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                </div>

                <h1 className="result-title">Payment Failed</h1>
                
                <div className="result-details">
                    <p className="result-message">
                        {errorMessage}
                    </p>
                    
                    <div className="troubleshooting">
                        <h2>Common Issues</h2>
                        <ul>
                            <li>Your card was declined by the issuing bank</li>
                            <li>Insufficient funds in your account</li>
                            <li>Incorrect card information was provided</li>
                            <li>The transaction was flagged as suspicious by your bank</li>
                        </ul>
                    </div>

                    <div className="help-info">
                        <h2>Need Help?</h2>
                        <p>
                            If you continue to experience issues, please contact our support team at
                            <a href="mailto:support@koreanmate.app"> support@koreanmate.app</a>.
                        </p>
                    </div>
                </div>

                <div className="action-buttons">
                    <button onClick={onRetry} className="retry-button">
                        Try Again
                    </button>
                    <button onClick={onClose} className="home-button">
                        Close
                    </button>
                </div>
            </div>
        </Modal>
    );
};